import { useContext, useReducer, createContext, useCallback } from "react";
import { useRequest } from "../../services/request";

const Context = createContext();

export function useCompany() {
  return useContext(Context);
}

const initState = {
  list: [],
  Names: [],
  listSubCompanies: [],
  listAllCompanies: [],
  selected: null,
  selectedfile: null,
  selectedTab: "subCompanies",
  count: 0,
  page: 1,
  perPage: 10,
  totalSize: 0,
  status: "idle",
  error: null,
};
const reducer = (state, action) => {
  switch (action.type) {
    case "set_list":
      return { ...state, list: [...action.payload] };
    case "set_Names":
      return { ...state, Names: [...action.payload] };
    case "set_listAllCompanies":
      return { ...state, listAllCompanies: [...action.payload] };
    case "set_selected":
      return { ...state, selected: { ...action.payload } };
    case "set_selectedfile":
      return { ...state, selectedfile: { ...action.payload } };
    case "set_count":
      return { ...state, count: action.payload };
    case "set_selected_tab":
      return { ...state, selectedTab: action.payload };
    case "add":
      const newList = [{ ...action.payload }, ...state.list];
      return { ...state, list: newList };
    case "edit":
      const modified = state.list.map((p) =>
        p.id === action.payload.id ? { ...p, ...action.payload } : p
      );
      return { ...state, list: modified };
    case "editCurrency": {
      const { usdPrice, euroPrice } = action.payload;
      const newSelected = {
        ...state.selected,
        currency: { usdPrice: usdPrice, euroPrice: euroPrice },
      };
      return { ...state, selected: newSelected };
    }
    case "delete":
      const filtered = state.list.filter((p) => p.id !== action.payload);
      return { ...state, list: filtered };
    case "deleteCurrency":
      const newSelected = {
        ...state.selected,
        currency: null,
      };
      return { ...state, selected: newSelected };
    case "status":
      return { ...state, status: action.payload };
    case "set_totalSize":
      return { ...state, totalSize: action.payload };
    case "set_page":
      return { ...state, page: action.payload };
    case "set_per_page":
      return { ...state, perPage: action.payload };
    case "set_listSubCompanies":
      return { ...state, listSubCompanies: [...action.payload] };

    default:
      throw new Error(`Invalid dispatch type: ${action.type}`);
  }
};

export default function CompanyProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initState);
  const req = useRequest();

  const fetchList = useCallback(
    async (pageSize = state.perPage, pageN = 1) => {
      if (pageSize === 0) pageSize = 10;
      let headersData = {
        pageNumber: state.page,
        pageSize: pageSize,
      };
      if (pageN !== 1) {
        headersData.pageNumber = pageN;
      }
      dispatch({ type: "status", payload: "fetching" });

      const resData = await req(`Company/List`, null, {}, true, headersData);
      dispatch({ type: "status", payload: `idle` });
      dispatch({ type: "set_list", payload: resData.data.companies || [] });
      dispatch({ type: "set_count", payload: resData.data.totalPages || 1 });
      dispatch({ type: "set_totalSize", payload: resData.data.totalSize || 0 });
      if (pageSize === 100)
        dispatch({
          type: "set_listAllCompanies",
          payload: resData.data.companies || [],
        });
    },
    [req, state.page, state.perPage]
  );
  /** fetch names companies */
  const fetchNames = useCallback(async () => {
    dispatch({ type: "status", payload: "fetching" });
    const resData = await req(`Company/Names`, null, {}, true, {});
    dispatch({ type: "status", payload: `idle` });
    dispatch({ type: "set_Names", payload: resData.data || [] });
  }, [req]);

  const fetchOne = useCallback(
    (id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `fetching` });
        const resData = await req(`Company/Info/${id}`, null, {}, true);
        dispatch({ type: "set_selected", payload: resData.data });
        dispatch({ type: "status", payload: `idle` });
        resolve(resData);
      });
    },
    [req]
  );
  const fetchSubCompaniesforCompany = useCallback(
    (id, page = 1, perPage = 10) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `fetching` });
        let headersData = {
          pageNumber: Number(page + 1),
          pageSize: perPage,
        };
        const resData = await req(
          `SubCompany/List/${id}`,
          null,
          {},
          headersData
        );
        dispatch({
          type: "set_listSubCompanies",
          payload: resData.data.subCompanies,
        });
        dispatch({ type: "status", payload: `idle` });
        resolve(resData);
      });
    },
    [req]
  );
  const create = useCallback(
    async (data) => {
      return new Promise(async (resolve, reject) => {
        try {
          const newData = { ...data };
          const resData = await req(
            "Company/Create",
            newData,
            { method: "POST" },
            true
          );
          dispatch({ type: "add", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );
  const deleting = useCallback(
    async (id) => {
      return new Promise(async (resolve, reject) => {
        try {
          const resData = await req(
            `Company/Delete/${id}`,
            null,
            { method: "DELETE" },
            true
          );
          dispatch({ type: "delete", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );
  const edit = useCallback(
    async (id, data) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `Company/Update/${id}`,
            data,
            { method: "PUT" },
            true
          );
          dispatch({ type: "edit", payload: resData });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );
  const uploadPhoto = useCallback(
    async (data, id) => {
      let formData = new FormData();
      formData.append("file", data);
      formData.append("id", id);
      return new Promise(async (resolve, reject) => {
        try {
          const resData = await req(
            "Upload/Logo",
            formData,
            { method: "POST", headers: {} },
            true
          );
          resolve(resData.data);
        } catch (error) {
          console.log(error);
          reject(error);
        }
      });
    },
    [req]
  );
  const CreateCurrency = useCallback(
    async (id, data) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `Company/SetCurrency/${id}`,
            data,
            { method: "PUT" },
            true
          );
          dispatch({ type: "editCurrency", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );
  const deleteCurrency = useCallback(
    async (id) => {
      return new Promise(async (resolve, reject) => {
        try {
          const resData = await req(
            `Company/DeleteCurrency/${id}`,
            null,
            { method: "DELETE" },
            true
          );
          dispatch({ type: "deleteCurrency", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );
  return (
    <Context.Provider
      value={{
        state,
        fetchList,
        fetchNames,
        fetchOne,
        fetchSubCompaniesforCompany,
        edit,
        create,
        deleting,
        uploadPhoto,
        dispatch,
        CreateCurrency,
        deleteCurrency,
      }}
    >
      {children}
    </Context.Provider>
  );
}
